import { MessageSquare, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

const BAR_HEIGHT = 72;
const BOTTOM_OVERLAY_CLEARANCE = 96;
const SELECTION_HANDLE_CLEARANCE = 20;
const SELECTION_TOOLBAR_CLEARANCE = 76;
const HEADER_CLEARANCE = 64;

/** Position the touch control outside the selection handles and bottom browser chrome. */
export function touchThreadSelectionBarPosition(
  rect: Pick<DOMRect, "top" | "bottom">,
  viewportHeight: number,
): { top: number } {
  const belowTop = rect.bottom + SELECTION_HANDLE_CLEARANCE;
  if (belowTop + BAR_HEIGHT <= viewportHeight - BOTTOM_OVERLAY_CLEARANCE) {
    return { top: belowTop };
  }

  const aboveTop = rect.top - SELECTION_TOOLBAR_CLEARANCE - BAR_HEIGHT;
  return { top: Math.max(HEADER_CLEARANCE, aboveTop) };
}

interface ThreadSelectionBarProps {
  sourceQuote: string;
  position?: { top: number } | null;
  label: string;
  dismissLabel: string;
  creating: boolean;
  disabled?: boolean;
  onClick: () => void;
  onDismiss: () => void;
}

/** Touch affordance kept clear of the OS text-selection toolbar. */
export function ThreadSelectionBar({
  sourceQuote,
  position,
  label,
  dismissLabel,
  creating,
  disabled = false,
  onClick,
  onDismiss,
}: ThreadSelectionBarProps) {
  const preserveSelection = (event: React.MouseEvent | React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      data-thread-create-button
      data-testid="thread-selection-bar"
      onPointerDown={preserveSelection}
      onMouseDown={preserveSelection}
      className={position
        ? "fixed inset-x-3 z-[70]"
        : "fixed inset-x-3 bottom-0 z-[70] pb-[env(safe-area-inset-bottom)]"}
      style={position ? { top: position.top } : undefined}
    >
      <div className="mx-auto flex max-w-2xl items-center gap-2 rounded-t-xl border bg-background/95 p-2 shadow-lg backdrop-blur">
        <p className="min-w-0 flex-1 rounded-md bg-muted px-2 py-1.5 text-xs text-muted-foreground line-clamp-2 break-words">
          {sourceQuote}
        </p>
        <Button
          type="button"
          size="sm"
          onPointerDown={preserveSelection}
          onMouseDown={preserveSelection}
          onClick={onClick}
          disabled={creating || disabled}
          aria-label={label}
          aria-busy={creating}
          data-thread-create-button
          data-testid="selection-bar-start-thread-button"
          className="h-auto shrink-0 gap-1.5 px-2.5 py-1.5 text-xs"
        >
          {creating ? <Spinner className="size-3" /> : <MessageSquare className="size-3.5" />}
          {label}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onPointerDown={preserveSelection}
          onMouseDown={preserveSelection}
          onClick={onDismiss}
          aria-label={dismissLabel}
          data-thread-create-button
          data-testid="dismiss-thread-selection-button"
          className="size-8 shrink-0"
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
}
